// The Allocation Plan importer (PRD §8 P1, Appendix B: `Allocation Plan 2026` → projects,
// assignments and weekly allocations). Chris copies the table in Excel and pastes it into the app.
// Nothing is uploaded and nothing is stored until the import runs, through the same endpoints the
// editor uses (SEC11: real hours go through the app, never through this repo).
//
// The workbook's columns are found from its header row: Status, Project, Resource, and one column
// per month holding average hours a week, the way the month zoom reads (PE4). Every match can be
// corrected on screen before anything is saved.

import { addDays, daysBetween, firstDayOf, lastDayOf, mondayOf, monthOf, type Iso, type Month } from "./dates";
import { periodsOf } from "./estimate";
import { emptyPlan, MAX_WEEKS, resizePlan, type Assignment, type WorkingPlan } from "./projects";
import type { ProjectStatus } from "./status";

/** Tab-separated text as Excel copies it; a quoted cell may hold tabs, line breaks and doubled quotes. */
export function parsePasted(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"' && cell === "") quoted = true;
    else if (ch === "\t") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else cell += ch;
  }
  if (cell !== "" || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.map((r) => r.map((c) => c.trim()));
}

const MONTHS = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
const pad = (m: number) => String(m).padStart(2, "0");
const fullYear = (y: string) => (y.length === 2 ? 2000 + Number(y) : Number(y));
/** 1–12 for a month name or its abbreviation of three letters or more ("Sept"), else 0. */
const monthName = (s: string) => {
  const l = s.toLowerCase();
  return l.length >= 3 ? MONTHS.findIndex((m) => m.startsWith(l)) + 1 : 0;
};

/**
 * A month from a header cell: 2026-01, 1/1/2026, 1/2026, Jan-26, Jan 2026, January 2026, an Excel
 * date serial, or a bare month name in `defaultYear`. Anything else is not a month column.
 */
export function parseMonth(text: string, defaultYear: number): Month | null {
  const t = text.trim();
  let m: RegExpMatchArray | null;
  if ((m = t.match(/^(\d{4})[-/](\d{1,2})(?:[-/]\d{1,2})?$/))) return valid(Number(m[1]), Number(m[2]));
  if ((m = t.match(/^(\d{1,2})\/\d{1,2}\/(\d{2}|\d{4})$/))) return valid(fullYear(m[2]), Number(m[1]));
  if ((m = t.match(/^(\d{1,2})\/(\d{4})$/))) return valid(Number(m[2]), Number(m[1]));
  if ((m = t.match(/^([A-Za-z]{3,9})\.?[-\s']+(\d{2}|\d{4})$/))) return valid(fullYear(m[2]), monthName(m[1]));
  if ((m = t.match(/^\d{1,2}-([A-Za-z]{3,9})-(\d{2}|\d{4})$/))) return valid(fullYear(m[2]), monthName(m[1]));
  if ((m = t.match(/^([A-Za-z]{3,9})$/))) return valid(defaultYear, monthName(m[1]));
  if ((m = t.match(/^(\d{5})$/))) {
    const serial = Number(m[1]);
    if (serial < 36_526 || serial > 73_050) return null; // 2000–2099
    return monthOf(addDays("1899-12-30", serial));
  }
  return null;
}

function valid(year: number, month: number): Month | null {
  return year >= 2000 && year <= 2099 && month >= 1 && month <= 12 ? `${year}-${pad(month)}` : null;
}

export interface ImportLayout {
  /** Index of the header row. */
  header: number;
  status: number | null;
  /** A separate code column, when the workbook has one. */
  code: number | null;
  project: number | null;
  resource: number | null;
  role: number | null;
  months: { col: number; month: Month }[];
}

/** Finds the header row (within the first 15) and what each column holds. */
export function detectLayout(rows: string[][], defaultYear: number): ImportLayout | null {
  let best: { layout: ImportLayout; score: number } | null = null;
  for (let r = 0; r < Math.min(rows.length, 15); r++) {
    const cells = rows[r];
    const find = (re: RegExp, except: (number | null)[] = []) => {
      const i = cells.findIndex((c, j) => re.test(c) && !except.includes(j));
      return i >= 0 ? i : null;
    };
    const status = find(/^status$|stage/i);
    const code = find(/\bcode\b/i);
    const project = find(/project|engagement|opportunity|client/i, [code]);
    const resource = find(/resource|person|people|staff|consultant|^name$|employee/i, [project, code]);
    const role = find(/^role|title|position/i, [project, code, resource]);
    const months = cells.map((c, col) => ({ col, month: parseMonth(c, defaultYear) })).filter((x): x is { col: number; month: Month } => !!x.month);
    const score = (project !== null || code !== null ? 2 : 0) + (resource !== null ? 2 : 0) + (status !== null ? 1 : 0) + Math.min(months.length, 6);
    if (months.length && (project !== null || code !== null) && (!best || score > best.score)) {
      best = { layout: { header: r, status, code, project, resource, role, months }, score };
    }
  }
  return best?.layout ?? null;
}

export interface ImportLine {
  /** 1-based row in the pasted table, for pointing back at the workbook. */
  row: number;
  status: string;
  project: string;
  resource: string;
  role: string;
  hours: { month: Month; perWeek: number }[];
}

const isTotal = (s: string) => /^(grand\s+)?totals?\b/i.test(s);

/**
 * The data rows under the header. A blank Project or Status cell carries the one above it, as an
 * outline-style table leaves them blank. Blank, total and all-zero rows are left out.
 */
export function readLines(rows: string[][], layout: ImportLayout): { lines: ImportLine[]; skipped: number } {
  const cell = (r: string[], c: number | null) => (c === null ? "" : (r[c] ?? "").trim());
  const lines: ImportLine[] = [];
  let skipped = 0;
  let lastProject = "";
  let lastStatus = "";
  for (let i = layout.header + 1; i < rows.length; i++) {
    const r = rows[i];
    if (r.every((c) => !c)) continue;
    const named = [cell(r, layout.code), cell(r, layout.project)].filter(Boolean).join(" ");
    const project = named || lastProject;
    const status = cell(r, layout.status) || (named ? "" : lastStatus);
    if (named) lastProject = named;
    if (cell(r, layout.status)) lastStatus = cell(r, layout.status);
    const resource = cell(r, layout.resource);
    if (isTotal(project) || isTotal(resource) || (!named && !resource)) {
      skipped++;
      continue;
    }
    const hours = layout.months
      .map(({ col, month }) => ({ month, perWeek: Number((r[col] ?? "").replace(/[,\s]/g, "").replace(/^-$/, "0")) || 0 }))
      .filter((h) => h.perWeek > 0);
    if (!hours.length) {
      skipped++;
      continue;
    }
    lines.push({ row: i + 1, status, project, resource, role: cell(r, layout.role), hours });
  }
  return { lines, skipped };
}

/** The workbook's status words, read as the app's statuses. */
export function statusFromText(text: string): ProjectStatus | null {
  const t = text.toLowerCase();
  if (/cancel/.test(t)) return "cancelled";
  if (/lost|declin/.test(t)) return "lost";
  if (/deliver|complete|done|closed/.test(t)) return "delivered";
  if (/book|won|signed|active|sold|committed/.test(t)) return "booked";
  if (/pend|verbal|sow/.test(t)) return "pending";
  if (/opp/.test(t)) return "opportunity";
  if (/prosp|lead|pipeline/.test(t)) return "prospect";
  return null;
}

/** A project code in the text: a known one anywhere, or else a leading letters-and-digits code like LG002. */
export function codeIn(text: string, known: ReadonlySet<string>): string | null {
  const tokens = text.toUpperCase().match(/[A-Z0-9]{2,12}/g) ?? [];
  const hit = tokens.find((t) => known.has(t));
  if (hit) return hit;
  const lead = text.trim().toUpperCase().match(/^([A-Z0-9]{2,12})\b/)?.[1];
  return lead && /\d/.test(lead) && /[A-Z]/.test(lead) ? lead : null;
}

/** The project's name once its code and separators are taken off. */
export const nameWithoutCode = (text: string, code: string | null) =>
  (code ? text.replace(new RegExp(`\\b${code}\\b`, "i"), "") : text).replace(/^[\s\-–—:|·]+|[\s\-–—:|·]+$/g, "").trim();

const norm = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/** A resource cell to a person: email, full name, "Last, First", a Pay ID alias, or a unique first or last name. */
export function matchPerson(text: string, people: readonly { id: string; name: string; payIds?: string[]; email?: string }[]): string | null {
  const t = text.trim();
  if (!t) return null;
  const unique = (found: readonly { id: string }[]) => (found.length === 1 ? found[0].id : null);
  const lower = t.toLowerCase();
  const words = norm(t.includes(",") ? t.split(",").reverse().join(" ") : t).split(" ");
  return (
    (t.includes("@") ? unique(people.filter((p) => p.email?.toLowerCase() === lower)) : null) ??
    unique(people.filter((p) => (p.payIds ?? []).some((id) => id.toLowerCase() === lower))) ??
    unique(people.filter((p) => norm(p.name) === words.join(" "))) ??
    (words.length === 1 ? unique(people.filter((p) => norm(p.name).split(" ").includes(words[0]))) : null) ??
    // Pay IDs such as Carlson_2026-1 name the person by surname.
    unique(people.filter((p) => (p.payIds ?? []).some((id) => words.includes(norm(id.split("_")[0])))))
  );
}

/** A role named in the text ("TBD consultant"): the longest role name it contains. */
export function matchRole(text: string, roles: readonly { id: string; name: string }[]): string | null {
  const t = norm(text);
  const hits = roles.filter((r) => t.includes(norm(r.name))).sort((a, b) => b.name.length - a.name.length);
  return hits[0]?.id ?? null;
}

/** The plan weeks for a run of months: each week belongs to the month of its Thursday (PE4). */
export function weeksForMonths(first: Month, last: Month): { startMonday: Iso; weeks: number } {
  let start = mondayOf(firstDayOf(first));
  if (addDays(start, 3) < firstDayOf(first)) start = addDays(start, 7);
  let weeks = 0;
  while (addDays(start, weeks * 7 + 3) <= lastDayOf(last)) weeks++;
  return { startMonday: start, weeks };
}

/** A workbook row once its resource is matched: a person or a placeholder, a role, and monthly hours. */
export interface ImportedRow {
  personId: string | null;
  roleId: string;
  hours: { month: Month; perWeek: number }[];
}

/**
 * The plan an import produces. With no existing plan, it spans the rows' months. Into an existing
 * plan, the plan widens to cover them and its staffing is replaced by the rows; its payment
 * schedule and other costs are kept.
 */
export function importedPlan(rows: readonly ImportedRow[], existing: WorkingPlan | null, newId: () => string): WorkingPlan {
  const months = rows.flatMap((r) => r.hours.map((h) => h.month)).sort();
  if (!months.length) throw new Error("There are no hours to import.");
  const span = weeksForMonths(months[0], months.at(-1)!);
  const lastOf = (p: { startMonday: Iso; weeks: number }) => addDays(p.startMonday, (p.weeks - 1) * 7);
  const start = existing && existing.startMonday < span.startMonday ? existing.startMonday : span.startMonday;
  const end = existing && lastOf(existing) > lastOf(span) ? lastOf(existing) : lastOf(span);
  const weeks = Math.round(daysBetween(start, end) / 7) + 1;
  if (weeks > MAX_WEEKS) throw new Error(`The plan would run ${weeks} weeks; the most is ${MAX_WEEKS}.`);
  const base = existing ? resizePlan(existing, start, weeks) : emptyPlan(start, weeks);
  const offset = Math.round(daysBetween(start, span.startMonday) / 7);
  const assignments: Assignment[] = rows.map((r) => {
    const hours = new Array(weeks).fill(0);
    hoursByWeek(span.startMonday, span.weeks, r.hours).forEach((h, w) => (hours[offset + w] = h));
    return { id: newId(), roleId: r.roleId, personId: r.personId, note: "From the Allocation Plan", hours };
  });
  return { ...base, assignments };
}

/** Hours a week for each week of a plan, from a line's monthly averages. */
export function hoursByWeek(startMonday: Iso, weeks: number, hours: readonly { month: Month; perWeek: number }[]): number[] {
  const byMonth = new Map(hours.map((h) => [h.month, h.perWeek]));
  const out = new Array(weeks).fill(0);
  for (const period of periodsOf({ startMonday, weeks }, "month")) for (const w of period.weeks) out[w] = byMonth.get(period.key as Month) ?? 0;
  return out;
}
